import { AttributeValue, CategoryPath, Customer } from "@msdyn365-commerce/retail-proxy";
import { ICoreContext, IRequestContext } from '@msdyn365-commerce/core';
import get from 'lodash/get';
import nbNO from './locale/nb-NO.json';
import enUS from './locale/en-US.json';
import { AttributeKey, ProductSearchResultExt, SimpleProductExt } from '../types';
import { decodeBase64String, isBase64EncodePrefixed } from "../base64";
import { getProductAgreements } from "../agreement-product-utils";
import { getTemperatureZone } from "./get-temperature-zone";

let cachedLocale: typeof nbNO;
let cachedLocaleFallback: typeof nbNO;

export enum ProductClassification {
    A = 'A',
    B = 'B',
    C = 'C',
    D = 'D'
}

function ensureLocale(locale: string) {
    if (!cachedLocale) {
        cachedLocale = getAttributesLocale(locale);
    }
    if (!cachedLocaleFallback) {
        cachedLocaleFallback = getAttributesLocale(locale === 'en-US' ? 'nb-NO' : 'en-US');
    }
}

export function getAttributeName(locale: string, attributeName: AttributeKey): string {
    ensureLocale(locale);
    return get(cachedLocale, attributeName);
}

export function findAttribute(locale: string, attributes: AttributeValue[] | undefined, attributeName: AttributeKey): AttributeValue | undefined {
    if (!attributes) {
        return undefined;
    }

    ensureLocale(locale);

    const name = get(cachedLocale, attributeName);
    const nameFallback = get(cachedLocaleFallback, attributeName);
    const attribute = attributes?.find(x => x.Name === name || x.Name === nameFallback);

    return attribute;
}

export function getAttributeText(locale: string, attributeName: AttributeKey): string | undefined {
    ensureLocale(locale);

    const attribute = get(cachedLocale, attributeName);
    return attribute;
}

export function getAttributeBooleanOrDefault(locale: string, attributes: AttributeValue[] | undefined, attributeName: AttributeKey, defaultValue: boolean = false): boolean {
    const attribute = findAttribute(locale, attributes, attributeName);

    if (!attribute) {
        return defaultValue;
    }

    return attribute.BooleanValue || defaultValue;
}

export function getAttributeNumberOrDefault(locale: string, attributes: AttributeValue[] | undefined, attributeName: AttributeKey, defaultValue: number): number {
    const attribute = findAttribute(locale, attributes, attributeName);

    if (!attribute) {
        return defaultValue;
    }

    let result: number | undefined;

    switch (attribute.DataTypeValue) {
        case 1: // Currency
            result = attribute.CurrencyValue;
            break;
        case 3: // Decimal
            result = attribute.FloatValue;
            break;
        case 4: // Integer
            result = attribute.IntegerValue;
            break;
        case 0: // None
        case 2: // DateTime
        case 5: // Text
        case 6: // TrueFalse
            result = defaultValue;
            break;
    }

    return result || 0;
}

export function getAttributeStringOrDefault<T>(locale: string, attributes: AttributeValue[] | undefined, attributeName: AttributeKey, defaultValue: T, suffix?: string): T | string {
    const attribute = findAttribute(locale, attributes, attributeName);

    if (!attribute) {
        return defaultValue;
    }

    let result;

    switch (attribute?.DataTypeValue) {
        case 0: // None
            result = `-`;
            break;
        case 1: // Currency
            result = attribute.CurrencyValue?.toString();
            break;
        case 2: // DateTime
            result = attribute.DateTimeOffsetValue?.toUTCString();
            break;
        case 3: // Decimal
            result = attribute.FloatValue?.toString();
            break;
        case 4: // Integer
            result = attribute.IntegerValue?.toString();
            break;
        case 5: // Text
            result = attribute.TextValue?.toString();
            break;
        case 6: // TrueFalse
            result = attribute.BooleanValue?.toString();
            break;
        default:
            result = '';
            break;
    }

    if (result && isBase64EncodePrefixed(attribute)) {
        result = decodeBase64String(result);
    }

    if (result && result !== '-' && suffix) {
        result += suffix;
    }

    return result || '';
}

export function getAttributesLocale(lang: string): typeof nbNO {
    switch (lang) {
        case 'nb-NO':
            return nbNO;
        case 'en-US':
            return {...nbNO, ...enUS};
        default:
            return nbNO;
    }
}

export function hasAccessToBuy(context: ICoreContext, customer: Customer | undefined, productAttributes: AttributeValue[] | undefined) {
    if (!productAttributes || productAttributes.length === 0) {
        // No product attributes available, default to true
        return true;
    }

    const locale = getAttributesLocale(context.request.locale);
    const licensesToCheck = [
        locale.alcoholProduct,
        locale.bama,
        locale.cocacola,
        locale.dutyFreeSugar,
        locale.nortura,
        locale.tobacco,
        locale.transit
    ];
    let hasAccess = true;

    const forsvaretAttributeId = context.request.app.config?.forsvaretAttributeRecordId;
    const hkjoottavtaleAttributeRecordId = context.request.app.config?.hkjoottavtaleAttributeRecordId;

    if (forsvaretAttributeId) {
        const productForsvaretAttribute = productAttributes?.find(x => x.RecordId === Number(forsvaretAttributeId))?.IntegerValue || 0;
        const customerForsvaretAttribute = customer?.Attributes?.find(x => x.RecordId === Number(forsvaretAttributeId))?.AttributeValue?.IntegerValue || 0;

        // If customer has Forsvaret attribute set to 1 don't allow buying of products with Forsvaret attribute set to 0
        if (customerForsvaretAttribute === 1 && productForsvaretAttribute === 0) {
            return false;
        }
    }

    if (hkjoottavtaleAttributeRecordId) {
        const productHKjootAttribute = productAttributes?.find(x => x.RecordId === Number(hkjoottavtaleAttributeRecordId))?.IntegerValue || 0;
        const customerHKjootAttribute = customer?.Attributes?.find(x => x.RecordId === Number(hkjoottavtaleAttributeRecordId))?.AttributeValue?.IntegerValue || 0;

        // If customer has H-kjøttavtale attribute set to 1 don't allow buying of products with H-kjøttavtale attribute set to 0
        if (customerHKjootAttribute === 1 && productHKjootAttribute === 0) {
            return false;
        }
    }

    if (checkForSvalbard(context, customer, productAttributes) === false) {
        return false;
    }

    for (const license of licensesToCheck) {
        const productAttribute = Number(productAttributes?.find(x => x.Name === license)?.TextValue);
        const customerAttribute = Number(customer?.Attributes?.find(x => x.Name === license)?.AttributeValue?.StringValue);

        // Product doesn't have the attribute. Default to has access
        if (isNaN(productAttribute) || productAttribute === 0) {
            continue;
        }

        // Product has the attribute and it's more than 0, but customer doesn't have it at all. No access
        if (isNaN(customerAttribute)) {
            hasAccess = false;
            break;
        }

        // Product has the attribute, but customer has a lower value. No access
        if (customerAttribute < productAttribute) {
            hasAccess = false;
            break;
        }
    }

    return hasAccess;
}

function checkForSvalbard(context: ICoreContext, customer: Customer | undefined, productAttributes: AttributeValue[] | undefined) {
    const svalbardAttributeId = context.request.app.config?.svalbardAttributeRecordId;
    const blockedSvalbardAttributeId = context.request.app.config?.blockedSvalbardAttributeRecordId;

    if (!svalbardAttributeId || !blockedSvalbardAttributeId) {
        return true;
    }

    const customerSvalbardAttribute = customer?.Attributes?.find(x => x.RecordId === Number(svalbardAttributeId))?.AttributeValue?.IntegerValue || 0;
    const productSvalbardAttribute = productAttributes?.find(x => x.RecordId === Number(blockedSvalbardAttributeId))?.IntegerValue || 0;

    if (productSvalbardAttribute > 0 && customerSvalbardAttribute > 0) {
        return false;
    }

    return true;
}

function getProductClassification (priority: number): ProductClassification {
    if (priority >= 1000) {
        return ProductClassification.A;
    } else if (priority >= 500) {
        return ProductClassification.B;
    } else if (priority >= 0) {
        return ProductClassification.C;
    } else {
        return ProductClassification.D;
    }
}

export function applyAttributesToProduct(context: IRequestContext, product: ProductSearchResultExt | SimpleProductExt, attributes: AttributeValue[] | undefined, categoryPaths: CategoryPath[] | undefined): void {
    const locale = context.locale;

    if (attributes && attributes.length > 0) {
        const alcoholPercentage = getAttributeStringOrDefault(locale, attributes, 'alcoholpercentage', '-', '%');
        const alcoholProduct = getAttributeStringOrDefault(locale, attributes, 'alcoholProduct', '0');
        const brand = getAttributeStringOrDefault(locale, attributes, 'brand', undefined);
        const ImporterGrouping = getAttributeStringOrDefault(locale, attributes, 'importerGrouping', undefined);
        const manufacturer = getAttributeStringOrDefault(locale, attributes, 'manufacturer', undefined);
        const transit = getAttributeStringOrDefault(locale, attributes, 'transit', false) === '1';
        const temperatureZone = getTemperatureZone(locale, attributes);
        const agentImporter = getAttributeStringOrDefault(locale, attributes, 'agentImporter', undefined);
        const producer = getAttributeStringOrDefault(locale, attributes, 'producer', undefined);
        const specialBadges = getAttributeStringOrDefault(locale, attributes, 'specialBadges', undefined);
        const unspsc = getAttributeStringOrDefault(locale, attributes, 'unspsc', '12345678');
        const classification = getProductClassification(getAttributeNumberOrDefault(locale, attributes, 'priority', 0));

        const oun = context.apiSettings.oun?.toLowerCase();
        const fallbackBadge = getAttributeStringOrDefault(locale, attributes, `badge_${oun}` as unknown as AttributeKey, undefined);

        product.AlcoholPercentage = alcoholPercentage;
        product.AlcoholProduct = parseInt(alcoholProduct, 10) || 0;
        product.Brand = brand;
        product.Classification = classification;
        product.ImporterGrouping = ImporterGrouping;
        product.Manufacturer = manufacturer;
        product.Transit = transit;
        product.TemperatureZone = temperatureZone;
        product.Agreements = getProductAgreements(context, attributes);
        product.AgentImporter = agentImporter;
        product.Producer = producer;
        product.SpecialBadges = specialBadges;
        product.FallbackBadge = fallbackBadge;
        product.UNSPSC = unspsc;
    }

    // const primaryCategory = product.CategoryPaths?.[0]?.Segments?.[1]?.CategoryName;

    if (categoryPaths) {
        product.Category = categoryPaths[0]?.Segments?.slice(-1)?.[0]?.CategoryName;
        product.CategoryPaths = categoryPaths;
    }
}
